import {
	faShoppingCart,
	faStopwatch,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { formatDate, ItemSettingsService } from '@supply-mover-2-0/core';
import React, { useState } from 'react';
import {
	Icon,
	ResilientImage,
	Text,
	useThemeColors,
	useTranslation,
} from '../../index';
import { NoPermissionTooltip } from '../permissions/NoPermissionTooltip';

export interface CartItemData {
	_id: number;
	_fk_item__id: number;
	itemDescription: string;
	itemManufacturer?: string;
	itemNDC?: string;
	/**
	 * Item-side identifiers used to derive a display "item number":
	 * itemExternalId2 ?? itemExternalId ?? _fk_item__id.
	 * Surfaced separately (rather than precomputed) so each consumer
	 * controls fallback behaviour and we don't lose the raw values.
	 */
	itemExternalId?: string | null;
	itemExternalId2?: string | null;
	/** Total quantity on hand across the client's accessible warehouses. */
	quantityOnHand?: number;
	/** Earliest expiration date across the client's accessible warehouses. */
	soonestExpirationDate?: string | null;
	quantityOrdered: number;
	price: string | number;
	total: string | number;
	primaryImageUrl?: string;
	imageUrls?: Array<{ url: string; title?: string; sortOrder?: number }>;
	perOrderLimit?: number;
	minimumPrice?: string | number;
	/**
	 * When true, the line is flagged as a short-date item (item.isShortDate).
	 * Consumers gate this per their own rules (e.g. only when the Short Date
	 * order-submission check is enabled) — the card simply renders the badge
	 * when it's true.
	 */
	isShortDate?: boolean;
}

export interface CartItemConfig {
	mode: 'interactive' | 'readonly';
	showRemoveButton?: boolean;
	showQuantityControls?: boolean;
	showPriceInput?: boolean;
	showValidation?: boolean;
	displayQuantity?: number;
	displayPrice?: string | number;
	/**
	 * When true, the remove (X) button is rendered but disabled and wrapped
	 * in a permission tooltip. Used when the user lacks orders:create.
	 */
	removeDisabled?: boolean;
	/**
	 * Optional override for the tooltip message shown on the disabled
	 * remove button. Defaults to the standard "no permission" copy.
	 */
	removeDisabledTooltip?: string;
}

export interface CartItemActions {
	onQuantityChange?: (itemId: number, quantity: number) => void;
	onPriceChange?: (itemId: number, price: number) => void;
	onRemove?: (itemId: number) => void;
	onImageClick?: (item: CartItemData) => void;
}

export interface CartItemValidation {
	perOrderLimitError?: boolean;
	dailyLimitError?: boolean;
	monthlyLimitError?: boolean;
	priceBelowMinimum?: boolean;
	/**
	 * The short-date order-submission check fired on this line (item is short
	 * dated AND the check is enabled). Drives the row tint like the other
	 * item-level checks. Independent of the always-on "Short Date" badge.
	 */
	shortDate?: boolean;
	isWarningLevel?: boolean;
	validationComponent?: React.ReactNode;
}

interface SharedCartItemCardProps {
	item: CartItemData;
	config: CartItemConfig;
	actions?: CartItemActions;
	validation?: CartItemValidation;
	quantityControl?: React.ReactNode;
	priceControl?: React.ReactNode;
	/** Optional label shown below item metadata (e.g., shipping surcharge) */
	infoLabel?: React.ReactNode;
	className?: string;
	/**
	 * Invoked once when this item's image fails to load (typically a stale
	 * presigned S3 URL). Caller should refetch the parent cart payload so
	 * a freshly-signed `primaryImageUrl` is delivered. Bounded to a single
	 * retry per src by ResilientImage.
	 */
	onImageStaleUrl?: () => void;
}

export const SharedCartItemCard = React.memo(
	({
		item,
		config,
		actions,
		validation,
		quantityControl,
		priceControl,
		infoLabel,
		className = '',
		onImageStaleUrl,
	}: SharedCartItemCardProps) => {
		const colors = useThemeColors();
		const { t } = useTranslation();
		const [showImageModal, setShowImageModal] = useState(false);

		const displayQuantity = config.displayQuantity ?? item.quantityOrdered;
		const displayPrice = config.displayPrice ?? item.price;
		const itemTotal =
			typeof item.total === 'string' ? parseFloat(item.total) : item.total;

		const hasViolation =
			validation?.perOrderLimitError ||
			validation?.dailyLimitError ||
			validation?.monthlyLimitError ||
			validation?.priceBelowMinimum ||
			validation?.shortDate;
		const isWarningLevel = validation?.isWarningLevel ?? false;

		// Check if ALL violations are warning level - if any are errors, error styling takes precedence
		const isOnlyWarningLevel = hasViolation && isWarningLevel;

		// Subtle background tint for cards with violations
		const backgroundColor = hasViolation
			? isOnlyWarningLevel
				? colors.classes.bgWarning
				: colors.classes.bgDanger
			: colors.classes.bgPrimary;
		const borderColor = hasViolation
			? isOnlyWarningLevel
				? colors.classes.borderWarning
				: colors.classes.borderError
			: colors.classes.borderDefault;

		const handleImageClick = () => {
			if (actions?.onImageClick) {
				actions.onImageClick(item);
			} else {
				setShowImageModal(true);
			}
		};

		return (
			<div
				className={`transition-colors duration-200 relative overflow-hidden mb-3 border rounded-none ${backgroundColor} ${borderColor} ${className}`}
			>
				{/* Validation Expansion at top */}
				{validation?.validationComponent && (
					<div className="border-b border-gray-200 dark:border-gray-700">
						{validation.validationComponent}
					</div>
				)}

				{/* Card content */}
				<div className="p-2 md:p-3 relative">
					{/* Remove Button - Top Right Corner of content area. The
					    absolute positioning lives on the outer div so the
					    button's tooltip (when permission-disabled) anchors to
					    the visible button location instead of the start of
					    normal flow. */}
					{config.showRemoveButton && actions?.onRemove && (
						<div className="absolute -top-1 -right-1 z-20">
							<NoPermissionTooltip
								disabled={config.removeDisabled === true}
								message={config.removeDisabledTooltip}
								position="top"
							>
								<button
									onClick={
										config.removeDisabled === true
											? undefined
											: () => actions.onRemove?.(item._fk_item__id)
									}
									disabled={config.removeDisabled === true}
									className={`w-10 h-10 flex items-center justify-center touch-manipulation ${
										config.removeDisabled === true
											? 'opacity-40 cursor-not-allowed'
											: 'hover:opacity-60 cursor-pointer'
									}`}
									aria-label={t('Remove item')}
								>
									<Icon
										icon={faXmark}
										size={14}
										className={colors.classes.textSecondary}
									/>
								</button>
							</NoPermissionTooltip>
						</div>
					)}

					<div className="flex items-stretch min-h-[64px] sm:min-h-[80px]">
						{/* Product Image */}
						<div className="flex-shrink-0 mr-2 md:mr-3">
							{item.primaryImageUrl ? (
								<button onClick={handleImageClick} className="cursor-pointer">
									<ResilientImage
										src={item.primaryImageUrl}
										alt=""
										className="rounded-lg object-cover shadow-sm hover:opacity-80 transition-opacity w-16 h-16 sm:w-20 sm:h-20"
										style={{ display: 'block' }}
										onLikelyStaleUrl={onImageStaleUrl}
										fallback={
											<div
												className={`${colors.classes.bgSecondary} rounded-lg flex items-center justify-center shadow-inner w-16 h-16 sm:w-20 sm:h-20`}
											>
												<Icon
													icon={faShoppingCart}
													size={20}
													className={colors.classes.textTertiary}
												/>
											</div>
										}
									/>
								</button>
							) : (
								<div
									className={`${colors.classes.bgSecondary} rounded-lg flex items-center justify-center shadow-inner w-16 h-16 sm:w-20 sm:h-20`}
								>
									<Icon
										icon={faShoppingCart}
										size={20}
										className={colors.classes.textTertiary}
									/>
								</div>
							)}
						</div>

						{/* Product Details and Controls */}
						<div className={`flex-1 ${config.showRemoveButton ? 'pr-8' : ''}`}>
							{/* Product Info */}
							<div className="mb-3">
								<Text
									className={`text-sm md:text-base font-semibold ${colors.classes.textPrimary} line-clamp-2`}
								>
									{item.itemDescription}
								</Text>
								<div className="flex flex-col sm:flex-row sm:items-center mt-1">
									{item.itemManufacturer && (
										<Text
											className={`text-xs ${colors.classes.textSecondary} mr-2 truncate`}
										>
											{item.itemManufacturer}
										</Text>
									)}
									{item.itemNDC && (
										<Text
											className={`text-xs ${colors.classes.textTertiary} truncate`}
										>
											{t('NDC')}: {item.itemNDC}
										</Text>
									)}
								</div>
								{(() => {
									// Display "item number" preferring externalId2,
									// falling back to externalId, then the internal item id.
									const itemNumber =
										item.itemExternalId2 ??
										item.itemExternalId ??
										item._fk_item__id;
									const hasItemNumber = itemNumber != null;
									const hasQoh = item.quantityOnHand != null;
									const hasExp =
										item.soonestExpirationDate != null &&
										item.soonestExpirationDate !== '';
									const hasShortDate = item.isShortDate === true;
									if (!hasItemNumber && !hasQoh && !hasExp && !hasShortDate) {
										return null;
									}
									return (
										<div
											className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs ${colors.classes.textTertiary}`}
										>
											{hasItemNumber && (
												<Text>
													{t('Item #')}: {t(String(itemNumber))}
												</Text>
											)}
											{hasQoh && (
												<Text>
													{t('Qty On Hand')}: {t(String(item.quantityOnHand))}
												</Text>
											)}
											{hasExp && (
												<Text>
													{t('Exp')}:{' '}
													{t(formatDate(item.soonestExpirationDate))}
												</Text>
											)}
											{hasShortDate && (
												<span className="flex items-center gap-1 text-red-500">
													<Icon icon={faStopwatch} size={12} />
													<Text>{t('Short Date')}</Text>
												</span>
											)}
										</div>
									);
								})()}
								{infoLabel != null && <div className="mt-1">{infoLabel}</div>}
							</div>

							{/* Controls or Display */}
							{config.mode === 'interactive' ? (
								<div className="flex items-center gap-2">
									{config.showQuantityControls &&
										quantityControl &&
										quantityControl}
									{config.showPriceInput && priceControl && priceControl}
								</div>
							) : (
								<div className="flex items-center justify-between flex-wrap gap-2">
									<div className="flex items-center gap-4">
										<Text
											className={`${colors.classes.textPrimary} font-medium`}
										>
											{t('Qty')}: {displayQuantity}
										</Text>
										<Text className={`${colors.classes.textSecondary} text-sm`}>
											{t('@')} $
											{(() => {
												const maxDecimals =
													ItemSettingsService.getMaxPriceDecimalPrecision();
												const priceValue =
													typeof displayPrice === 'string'
														? displayPrice
														: displayPrice?.toFixed(maxDecimals) || '0.00';
												return priceValue;
											})()}
										</Text>
									</div>
									<Text
										className={`${colors.classes.textPrimary} font-semibold`}
									>
										$
										{itemTotal.toFixed(
											ItemSettingsService.getMaxPriceDecimalPrecision(),
										)}
									</Text>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison to prevent unnecessary re-renders
		const itemChanged =
			prevProps.item._id !== nextProps.item._id ||
			prevProps.item._fk_item__id !== nextProps.item._fk_item__id ||
			prevProps.item.itemDescription !== nextProps.item.itemDescription ||
			prevProps.item.itemManufacturer !== nextProps.item.itemManufacturer ||
			prevProps.item.itemNDC !== nextProps.item.itemNDC ||
			prevProps.item.itemExternalId !== nextProps.item.itemExternalId ||
			prevProps.item.itemExternalId2 !== nextProps.item.itemExternalId2 ||
			prevProps.item.quantityOnHand !== nextProps.item.quantityOnHand ||
			prevProps.item.soonestExpirationDate !==
				nextProps.item.soonestExpirationDate ||
			prevProps.item.quantityOrdered !== nextProps.item.quantityOrdered ||
			prevProps.item.price !== nextProps.item.price ||
			prevProps.item.total !== nextProps.item.total ||
			prevProps.item.primaryImageUrl !== nextProps.item.primaryImageUrl ||
			prevProps.item.perOrderLimit !== nextProps.item.perOrderLimit ||
			prevProps.item.minimumPrice !== nextProps.item.minimumPrice ||
			prevProps.item.isShortDate !== nextProps.item.isShortDate;

		const configChanged =
			prevProps.config.mode !== nextProps.config.mode ||
			prevProps.config.showRemoveButton !== nextProps.config.showRemoveButton ||
			prevProps.config.showQuantityControls !==
				nextProps.config.showQuantityControls ||
			prevProps.config.showPriceInput !== nextProps.config.showPriceInput ||
			prevProps.config.showValidation !== nextProps.config.showValidation ||
			prevProps.config.displayQuantity !== nextProps.config.displayQuantity ||
			prevProps.config.displayPrice !== nextProps.config.displayPrice ||
			prevProps.config.removeDisabled !== nextProps.config.removeDisabled ||
			prevProps.config.removeDisabledTooltip !==
				nextProps.config.removeDisabledTooltip;

		const validationChanged =
			prevProps.validation?.perOrderLimitError !==
				nextProps.validation?.perOrderLimitError ||
			prevProps.validation?.dailyLimitError !==
				nextProps.validation?.dailyLimitError ||
			prevProps.validation?.monthlyLimitError !==
				nextProps.validation?.monthlyLimitError ||
			prevProps.validation?.priceBelowMinimum !==
				nextProps.validation?.priceBelowMinimum ||
			prevProps.validation?.isWarningLevel !==
				nextProps.validation?.isWarningLevel ||
			prevProps.validation?.validationComponent !==
				nextProps.validation?.validationComponent;

		const otherChanged =
			prevProps.className !== nextProps.className ||
			prevProps.quantityControl !== nextProps.quantityControl ||
			prevProps.priceControl !== nextProps.priceControl;

		// Return true if props are equal (no re-render needed)
		const shouldRerender =
			itemChanged || configChanged || validationChanged || otherChanged;
		return !shouldRerender;
	},
);
